import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import * as transactionsSelectors from 'store/transactionsSlice/transactionsSelectors';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import useKeyNavigation from 'hooks/transaction/useKeyNavigation';
import {
    MINIMIZED_SIZE,
    MAXIMIZED_SIZE,
    MINIMIZED_POSITION,
    LOCALSTORAGE_KEY,
    HEADER_HEIGHT,
} from 'components/transaction/components/panels/transaction/VideoWallMode/constants';

const getInitialState = () => {
    const saved = localStorage.getItem(LOCALSTORAGE_KEY);
    if (saved) return JSON.parse(saved);
    const width = Math.min(MAXIMIZED_SIZE.width, window.innerWidth);
    const height = Math.min(MAXIMIZED_SIZE.height, window.innerHeight - HEADER_HEIGHT);
    return {
        width,
        height,
        top: Math.max(HEADER_HEIGHT, window.innerHeight / 2 - height / 2),
        left: Math.max(0, window.innerWidth / 2 - width / 2),
        minimized: false,
    };
};

const useCameraStreamWindow = ({ cameraStreamWindow, videoOutputId }) => {
    const dragOffset = useRef({ x: 0, y: 0 });
    const dragging = useRef(false);
    const dispatch = useDispatch();
    const [state, setState] = useState(getInitialState());
    const { isOpen } = cameraStreamWindow;
    const activeTab = useSelector(transactionsSelectors.selectActiveTab);
    const isCompactVideoWall = useSelector(transactionsSelectors.selectCompactVideoWallMode);

    const playSubStream = useCallback(() => {
        if (videoOutputId) cameraStreamManager.playStreamType('SUB', videoOutputId);
    }, [videoOutputId]);

    const onMouseMove = useCallback((e) => {
        if (!dragging.current) return;
        setState((prev) => {
            const maxLeft = window.innerWidth - prev.width;
            const maxTop = window.innerHeight - prev.height;
            return {
                ...prev,
                left: Math.max(0, Math.min(e.clientX - dragOffset.current.x, maxLeft)),
                top: Math.max(HEADER_HEIGHT, Math.min(e.clientY - dragOffset.current.y, maxTop)),
            };
        });
    }, []);

    const onMouseUp = useCallback(() => {
        dragging.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
    }, [onMouseMove]);

    const onMouseDown = useCallback(
        (e) => {
            dragging.current = true;
            dragOffset.current = {
                x: e.clientX - state.left,
                y: e.clientY - state.top,
            };
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [state.left, state.top, onMouseMove, onMouseUp]
    );

    const onResizeMouseDown = useCallback(
        (side) => (e) => {
            e.stopPropagation();
            const startX = e.clientX;
            const startY = e.clientY;
            const startWidth = state.width;
            const startHeight = state.height;
            const startLeft = state.left;

            const onResizeMouseMove = (ev) => {
                setState((prev) => {
                    const deltaX = ev.clientX - startX;
                    const deltaY = ev.clientY - startY;

                    let newState = { ...prev };
                    const aspectRatio = startWidth / startHeight;

                    const maxWidth = window.innerWidth - prev.left;
                    const maxHeight = window.innerHeight - prev.top - HEADER_HEIGHT;
                    const minWidth = MINIMIZED_SIZE.width;
                    const minHeight = MINIMIZED_SIZE.height;

                    if (side === 'right') {
                        let newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, maxWidth));
                        newState.width = newWidth;
                        newState.height = Math.max(minHeight, Math.min(newWidth / aspectRatio, window.innerHeight - prev.top));
                    }

                    if (side === 'left') {
                        let newLeft = startLeft + deltaX;
                        let newWidth = Math.max(minWidth, Math.min(startWidth - deltaX, window.innerWidth - newLeft));
                        if (newLeft < 0) {
                            newLeft = 0;
                            newWidth = startWidth + startLeft;
                        }
                        newState.left = newLeft;
                        newState.width = newWidth;
                        newState.height = Math.max(minHeight, Math.min(newWidth / aspectRatio, window.innerHeight - prev.top));
                    }

                    if (side === 'bottom') {
                        let newHeight = Math.max(minHeight, Math.min(startHeight + deltaY, maxHeight));
                        newState.height = newHeight;
                        newState.width = Math.max(minWidth, Math.min(newHeight * aspectRatio, window.innerWidth - prev.left));
                    }

                    if (newState.width > minWidth || newState.height > minHeight) {
                        newState.minimized = false;
                    }

                    if (newState.left + newState.width > window.innerWidth) {
                        newState.width = window.innerWidth - newState.left;
                        newState.height = Math.max(minHeight, newState.width / aspectRatio);
                    }
                    if (newState.top + newState.height > window.innerHeight) {
                        newState.height = window.innerHeight - newState.top;
                        newState.width = Math.max(minWidth, newState.height * aspectRatio);
                    }

                    return newState;
                });
            };

            const onResizeMouseUp = () => {
                document.removeEventListener('mousemove', onResizeMouseMove);
                document.removeEventListener('mouseup', onResizeMouseUp);
            };

            document.addEventListener('mousemove', onResizeMouseMove);
            document.addEventListener('mouseup', onResizeMouseUp);
        },
        [state.width, state.height, state.left]
    );

    const handleMinimize = useCallback(() => {
        setState((prev) => ({
            ...prev,
            minimized: true,
            width: MINIMIZED_SIZE.width,
            height: MINIMIZED_SIZE.height,
            left: window.innerWidth - MINIMIZED_SIZE.width - MINIMIZED_POSITION.right,
            top: window.innerHeight - MINIMIZED_SIZE.height - MINIMIZED_POSITION.bottom,
        }));
    }, []);

    const handleMaximize = useCallback(() => {
        const width = Math.min(MAXIMIZED_SIZE.width, window.innerWidth);
        const height = Math.min(MAXIMIZED_SIZE.height, window.innerHeight - HEADER_HEIGHT);
        setState((prev) => ({
            ...prev,
            minimized: false,
            width,
            height,
            left: Math.max(0, window.innerWidth / 2 - width / 2),
            top: Math.max(HEADER_HEIGHT, window.innerHeight / 2 - height / 2),
        }));
    }, []);

    const handleClose = useCallback(() => {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(state));
        dispatch(transactionActions.setCameraStreamWindow({ isOpen: false, camInfo: {} }));
        playSubStream();
    }, [dispatch, playSubStream, state]);

    useKeyNavigation([
        { altKey: true, shiftKey: false, code: 'ArrowDown', handleClick: handleMinimize },
        { altKey: true, shiftKey: false, code: 'ArrowUp', handleClick: handleMaximize },
        { code: 'Escape', handleClick: handleClose },
    ]);

    useEffect(() => {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(state));
    }, [state]);

    useEffect(() => {
        if (isOpen) setState(getInitialState());
    }, [isOpen]);

    useEffect(() => {
        if (activeTab !== 'wall' || !isCompactVideoWall) handleClose();
    }, [activeTab, handleClose, isCompactVideoWall]);

    useEffect(() => {
        const handleResize = () => {
            setState((prev) => {
                let width = Math.min(prev.width, window.innerWidth);
                let height = Math.min(prev.height, window.innerHeight - HEADER_HEIGHT);
                let left = Math.max(0, Math.min(prev.left, window.innerWidth - width));
                let top = Math.max(HEADER_HEIGHT, Math.min(prev.top, window.innerHeight - height));
                return { ...prev, width, height, left, top };
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    return { state, onMouseDown, onResizeMouseDown, handleMinimize, handleMaximize, handleClose };
};

export default useCameraStreamWindow;
